import { FieldErrors, FieldValues } from "react-hook-form";

import {
  isDeclineClass,
  messageForDeclineClass,
} from "@/lib/payments/decline-reason";

export async function parseError(
  error: any,
  defaultMessage: string = "An error occurred. Please try again.",
) {
  const detail = await readErrorDetail(error);

  if (typeof detail === "string") return detail;

  // FastAPI validation errors: [{loc: [...], msg: "..."}, ...]
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d: any) => {
        if (typeof d === "string") return d;
        if (d && typeof d.msg === "string") return d.msg;
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join("; ") : defaultMessage;
  }

  // A classified decline. `message` on these is the *processor's* own text,
  // which we deliberately do not show anyone: it is written in their register,
  // changes when they change it, and for a malformed request of ours it blames
  // the patient's card. Our copy for the class says the same thing in our
  // words. The raw text stays in the body for logs and support.
  if (
    detail &&
    typeof detail === "object" &&
    isDeclineClass(detail.decline_class)
  ) {
    return messageForDeclineClass(detail.decline_class);
  }

  // Coded errors from our own backend: { code, message }.
  if (
    detail &&
    typeof detail === "object" &&
    typeof detail.message === "string"
  ) {
    return detail.message;
  }

  return defaultMessage;
}

/** Reads `error.response.detail`, or the confirmed missing-tenant JSON string.
 *  Cache the body so later `parseError` / `parseErrorCode` calls don't re-read
 *  a consumed stream. Concurrent callers share the in-flight promise. */
export async function readErrorDetail(error: any): Promise<any> {
  if (!error?.response) return undefined;
  if (!("__parsedErrorBodyPromise" in error)) {
    const bodyPromise = error.response.json().catch(() => ({}));
    Object.defineProperty(error, "__parsedErrorBodyPromise", {
      value: bodyPromise,
      enumerable: false,
      configurable: false,
      writable: false,
    });
  }
  const body = await error.__parsedErrorBodyPromise;
  if (typeof body === "string") {
    // Other middleware strings are technical diagnostics, not patient copy.
    return error.response.status === 404 && body === "Tenant not found."
      ? body
      : undefined;
  }
  return body?.detail;
}

/** Tenant middleware runs before patient handlers. A 404 alone is NOT enough:
 *  claim endpoints can also return 404 for an unrelated missing patient. */
export async function isTenantNotFoundError(error: unknown): Promise<boolean> {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  return (
    status === 404 && (await readErrorDetail(error)) === "Tenant not found."
  );
}

/** Returns the `code` field on a coded error detail (`{ code, message }`),
 *  or undefined if the response doesn't carry one. */
export async function parseErrorCode(error: any): Promise<string | undefined> {
  const detail = await readErrorDetail(error);
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    return detail.code;
  }
  return undefined;
}

export function parseFormErrors(errors: FieldErrors<FieldValues>): string {
  if (!errors || Object.keys(errors).length === 0) {
    return "An error occurred. Please try again.";
  }

  const errorMessages: string[] = [];

  const extractErrors = (obj: any, prefix: string = "") => {
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === "object") {
        if ("message" in value) {
          errorMessages.push(value.message as string);
        } else {
          extractErrors(value, `${prefix}${key}.`);
        }
      }
    }
  };

  extractErrors(errors);

  return errorMessages.length > 0
    ? errorMessages.join("\n")
    : "An error occurred. Please try again.";
}
